// /src/components/admin/RoadmapTab.jsx
// Roadmap — the single working list of open items and who acts on each (admin-only).
// Brief: docs/briefs/2026-09-21-roadmap-one-list-phase1.md (Part C).
// The Architect reads it as ~/ournet-docs-mirror/ROADMAP.md (scripts/export-roadmap.py) and
// sends changes through briefs-inbox/ (scripts/apply-roadmap-updates.py).

import React, { useState, useEffect } from 'react';
import { useTheme } from '../../themes/ThemeProvider';
import { supabaseFetch } from '../../lib/supabase';
import { useTranslation } from '../../i18n/useTranslation';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { tokens } from '../../styles/designTokens';
import StandardButton from '../core/StandardButton';
import ConfirmationModal from '../core/ConfirmationModal';
import { GripVertical } from 'lucide-react';

// Vocabularies. These mirror the CHECK constraints on public.admin_roadmap
// (admin_roadmap_owner_check, _kind_check, _priority_check, _status_check —
// supabase/migrations/20260921_roadmap_one_list_reshape.sql). The constraints are the
// authority: change them first, then these lists. Array order is display order.
const OWNERS = ['michael', 'architect', 'cc', 'dan'];
const KINDS = ['decision', 'build', 'probe', 'ops', 'docs'];
const PRIORITIES = ['now', 'next', 'later'];
const STATUSES = ['open', 'in_progress', 'waiting', 'done'];

// A ref that is a repo path (starts with `docs/`) opens the file on GitHub; any other ref is text.
// Only the leading path is linked: a ref may carry trailing words after it ("… § GOVERNANCE"),
// which stay as text rather than being folded into the URL.
// Brief: docs/briefs/2026-10-14-roadmap-dan-as-owner.md, Part B.
const REPO_FILE_BASE = 'https://github.com/mb69342/OurNet/blob/main/';
const splitRepoPath = (entry) => {
  if (!entry.startsWith('docs/')) return null;
  const [path] = entry.split(/\s/, 1);
  return { path, rest: entry.slice(path.length) };
};

// A factory, not a constant: `refs` is an array, and one shared [] across every reset would
// let an edit to one form leak into the next.
const emptyForm = () => ({
  title: '',
  owner: 'michael',
  kind: 'build',
  priority: 'next',
  status: 'open',
  refs: [],
  notes: ''
});

// Status badge colours. No design token exists for these four states, so they are kept
// here in one place rather than inlined.
const STATUS_COLORS = {
  open: { bg: '#e0e7ff', text: '#4338ca' },
  in_progress: { bg: '#dcfce7', text: '#15803d' },
  waiting: { bg: '#fef3c7', text: '#b45309' },
  done: { bg: '#f3f4f6', text: '#374151' }
};

const RoadmapTab = ({ currentUser }) => {
  const theme = useTheme();
  const { t, formattingLocale } = useTranslation('admin');
  const { windowWidth } = useMediaQuery();
  const isPhone = windowWidth < tokens.breakpoints.phone;

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [showDone, setShowDone] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [refDraft, setRefDraft] = useState('');   // the references box, before Add
  const [infoModal, setInfoModal] = useState({ visible: false, message: '' });
  const [dragging, setDragging] = useState(null);     // the open item being dragged
  const [dropTarget, setDropTarget] = useState(null); // where it would land
  // Drag needs a mouse (brief change 5): touch screens and pens without hover get no handle.
  const [canDrag] = useState(() =>
    typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches
  );
  const showInfo = (message) => setInfoModal({ visible: true, message });

  // Literal t() calls (not t(variable)) so the translation audit can see every key —
  // TRANSLATION.md § Common Pitfalls 1.
  const labels = {
    owner: {
      michael: t('roadmapOwnerMichael'),
      architect: t('roadmapOwnerArchitect'),
      cc: t('roadmapOwnerCc'),
      dan: t('roadmapOwnerDan')
    },
    kind: {
      decision: t('roadmapKindDecision'),
      build: t('roadmapKindBuild'),
      probe: t('roadmapKindProbe'),
      ops: t('roadmapKindOps'),
      docs: t('roadmapKindDocs')
    },
    priority: {
      now: t('roadmapPriorityNow'),
      next: t('roadmapPriorityNext'),
      later: t('roadmapPriorityLater')
    },
    status: {
      open: t('roadmapStatusOpen'),
      in_progress: t('roadmapStatusInProgress'),
      waiting: t('roadmapStatusWaiting'),
      done: t('roadmapStatusDone')
    }
  };

  // Only the first load and Refresh show "Loading…". The reload after an edit or a drop is
  // silent, so the scrolling body keeps its position and the list never blanks.
  const loadItems = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data, error } = await supabaseFetch.query('admin_roadmap', {
        select: '*',
        order: 'item_no.asc'
      });

      if (error) {
        console.error('Error loading roadmap items:', error);
        return;
      }

      setItems(data || []);
    } catch (err) {
      console.error('Error loading roadmap items:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  // Only the editable columns are sent. item_no is GENERATED ALWAYS; updated_at and done_at
  // are set by the table's trigger, so the browser and the CC role paths behave the same.
  const payloadFrom = (data) => ({
    title: data.title.trim(),
    owner: data.owner,
    kind: data.kind,
    priority: data.priority,
    status: data.status,
    refs: data.refs,
    notes: data.notes.trim() || null
  });

  // Fold a typed-but-not-added reference into the list at submit time, so a reference the user
  // typed and did not press Add on is saved rather than silently dropped.
  const formWithDraft = () => {
    const entry = refDraft.trim();
    if (!entry || formData.refs.includes(entry)) return formData;
    return { ...formData, refs: [...formData.refs, entry] };
  };

  const addRef = () => {
    const entry = refDraft.trim();
    if (!entry) return;
    // Ruled by addendum 1: a list may not hold the same entry twice, and the panel refuses
    // rather than silently de-duplicating.
    if (formData.refs.includes(entry)) {
      showInfo(t('roadmapRefAlreadyPresent'));
      return;
    }
    setFormData({ ...formData, refs: [...formData.refs, entry] });
    setRefDraft('');
  };

  const removeRef = (index) =>
    setFormData({ ...formData, refs: formData.refs.filter((_, i) => i !== index) });

  // Explicit controls, not drag. The original reason was that the row drag above is mouse-only
  // (see canDrag) and a phone user would have had no way to reorder — that reason is void since
  // Michael ruled the Admin Panel desktop-only on 23 Sep 2026. The decision stands on its own
  // desktop merits: dragging one line within a form field list is heavier than two buttons, and
  // the list here is short.
  const moveRef = (index, delta) => {
    const next = [...formData.refs];
    const to = index + delta;
    if (to < 0 || to >= next.length) return;
    [next[index], next[to]] = [next[to], next[index]];
    setFormData({ ...formData, refs: next });
  };

  const handleAdd = async () => {
    if (!formData.title.trim()) return;
    try {
      const { error } = await supabaseFetch.insert('admin_roadmap', {
        ...payloadFrom(formWithDraft()),
        created_by: currentUser?.id
      });

      if (error) {
        console.error('Error adding roadmap item:', error);
        showInfo(t('failedToAddItem'));
        return;
      }

      setFormData(emptyForm());
      setRefDraft('');
      setShowAddForm(false);
      loadItems(true);
    } catch (err) {
      console.error('Error adding roadmap item:', err);
      showInfo(t('failedToAddItem'));
    }
  };

  // reload=false lets a multi-row renumber reload once at the end. A failure always reloads,
  // so an optimistic drag is put back where the database says it is.
  const updateItem = async (id, changes, reload = true) => {
    try {
      const { error } = await supabaseFetch.update('admin_roadmap', changes, { eq: { id } });

      if (error) {
        console.error('Error updating roadmap item:', error);
        showInfo(t('failedToUpdateItem'));
        loadItems(true);
        return false;
      }

      if (reload) loadItems(true);
      return true;
    } catch (err) {
      console.error('Error updating roadmap item:', err);
      showInfo(t('failedToUpdateItem'));
      loadItems(true);
      return false;
    }
  };

  const handleUpdate = async (id) => {
    if (!formData.title.trim()) return;
    const ok = await updateItem(id, payloadFrom(formWithDraft()));
    if (ok) cancelEdit();
  };

  const handleDelete = async (id) => {
    try {
      const { error } = await supabaseFetch.delete('admin_roadmap', { eq: { id } });

      if (error) {
        console.error('Error deleting roadmap item:', error);
        showInfo(t('failedToDeleteItem'));
        return;
      }

      loadItems(true);
    } catch (err) {
      console.error('Error deleting roadmap item:', err);
      showInfo(t('failedToDeleteItem'));
    }
  };

  const startEdit = (item) => {
    setEditingItem(item.id);
    setFormData({
      title: item.title,
      owner: item.owner,
      kind: item.kind,
      priority: item.priority,
      status: item.status,
      refs: Array.isArray(item.refs) ? [...item.refs] : [],
      notes: item.notes || ''
    });
    setRefDraft('');
    setShowAddForm(false);
  };

  const cancelEdit = () => {
    setEditingItem(null);
    setFormData(emptyForm());
    setRefDraft('');
  };

  // Position within a section: sort_key (20260921c), then item_no as a deterministic tie-break
  // and as the fallback if sort_key is ever missing.
  const byPosition = (a, b) =>
    (Number(a.sort_key ?? a.item_no) - Number(b.sort_key ?? b.item_no)) || (a.item_no - b.item_no);

  // Open items grouped by owner, then into now / next / later sections, each in stored order.
  const openItems = items.filter(item => item.status !== 'done');
  const groups = OWNERS.map(owner => ({
    owner,
    count: openItems.filter(item => item.owner === owner).length,
    sections: PRIORITIES.map(priority => ({
      priority,
      items: openItems
        .filter(item => item.owner === owner && item.priority === priority)
        .sort(byPosition)
    }))
  })).filter(group => group.count > 0);

  // Done items newest first.
  const doneItems = items
    .filter(item => item.status === 'done')
    .sort((a, b) => new Date(b.done_at || 0) - new Date(a.done_at || 0));

  // --- Drag-and-drop prioritisation (brief change 5) ---------------------------------------
  // Native HTML5 drag-and-drop, no library. Mouse only: gated on a fine hover-capable pointer,
  // so touch screens simply don't get a handle. Within one owner only; changing owner stays in
  // Edit. A drop writes ONE row: the moved item's new sort_key (the midpoint of its new
  // neighbours), plus its new priority when it lands in another section.

  const sectionItems = (owner, priority, excludeId) =>
    openItems
      .filter(item => item.owner === owner && item.priority === priority && item.id !== excludeId)
      .sort(byPosition);

  const keyBetween = (prev, next) => {
    if (prev == null && next == null) return 1;
    if (prev == null) return next - 1;
    if (next == null) return prev + 1;
    return (prev + next) / 2;
  };

  const DRAG_TYPE = 'application/x-ournet-roadmap'; // private type: a stray drop can't paste text

  const handleDrop = async (owner, priority, beforeId) => {
    const moved = dragging;
    setDragging(null);
    setDropTarget(null);
    if (!moved || moved.owner !== owner) return;

    const list = sectionItems(owner, priority, moved.id);
    const index = beforeId ? list.findIndex(item => item.id === beforeId) : list.length;
    const at = index < 0 ? list.length : index;
    const prev = at > 0 ? Number(list[at - 1].sort_key) : null;
    const next = at < list.length ? Number(list[at].sort_key) : null;
    const oldKey = Number(moved.sort_key);
    const sameSection = moved.priority === priority;

    // Dropped back where it already was: nothing to write.
    if (sameSection && at === sectionItems(owner, priority).findIndex(item => item.id === moved.id)) return;

    let key = keyBetween(prev, next);
    // A cross-section drop must never send the item's old key: the table's trigger would read
    // "priority changed, key unchanged" as "send to the bottom" and override the drop.
    if (!sameSection && key === oldKey) {
      key = next != null ? (key + next) / 2 : key + 0.5;
    }

    const snapshot = items;

    // No room left between neighbours (duplicate keys, or very many drops into one gap):
    // renumber the section, one write per item in it. Each write also refreshes that item's
    // updated_at through admin_roadmap_before_write; rare, and accepted.
    if ((prev != null && key <= prev) || (next != null && key >= next)) {
      const ordered = [...list.slice(0, at), moved, ...list.slice(at)];
      for (let i = 0; i < ordered.length; i++) {
        const isMoved = ordered[i].id === moved.id;
        let position = i + 1;
        // Same guard as above for the moved item: never its old key alongside a new priority.
        if (isMoved && !sameSection && position === oldKey) position = i + 0.5;
        const changes = { sort_key: position };
        if (isMoved && !sameSection) changes.priority = priority;
        if (!(await updateItem(ordered[i].id, changes, false))) {
          setItems(snapshot);
          return;
        }
      }
      loadItems(true);
      return;
    }

    const changes = sameSection ? { sort_key: key } : { priority, sort_key: key };
    // Show the move at once; the silent reload after the write confirms it. On failure the
    // previous list is put back even if the reload also fails.
    setItems(prevItems => prevItems.map(item => (item.id === moved.id ? { ...item, ...changes } : item)));
    if (!(await updateItem(moved.id, changes))) setItems(snapshot);
  };

  // The drop position is worked out from the pointer, over the section's own rows, every time:
  // the first row (other than the dragged one) whose middle is below the pointer is the one the
  // item lands before; past the last row, it lands at the end. Nothing is read from earlier
  // render state, so a fast drop or a drop in the indent column can't use a stale target.
  const resolveTarget = (e, owner, priority) => {
    const rows = [...e.currentTarget.querySelectorAll(':scope > [data-roadmap-row]')]
      .filter(row => row.dataset.itemId !== dragging?.id);
    const hit = rows.find(row => {
      const rect = row.getBoundingClientRect();
      return e.clientY < rect.top + rect.height / 2;
    });
    const beforeId = hit ? hit.dataset.itemId : null;
    const last = rows[rows.length - 1];
    return {
      key: `${owner}|${priority}|${beforeId ?? 'end'}`,
      owner,
      priority,
      beforeId,
      lineOn: beforeId ?? (last ? last.dataset.itemId : null),
      after: !beforeId
    };
  };

  const sectionDragProps = (owner, priority) => ({
    onDragOver: (e) => {
      if (!dragging || dragging.owner !== owner) return;
      e.preventDefault();
      const target = resolveTarget(e, owner, priority);
      if (dropTarget?.key !== target.key) setDropTarget(target);
    },
    onDrop: (e) => {
      e.preventDefault();
      if (!dragging || dragging.owner !== owner) return;
      const target = resolveTarget(e, owner, priority);
      handleDrop(target.owner, target.priority, target.beforeId);
    }
  });

  const styles = {
    // ModeColumn's structure (src/components/frontpage/columns/ModeColumn.jsx), copied locally as
    // ArticleDetail.jsx does: a fixed header above an independently scrolling body. The tab fills
    // the Admin Panel's content area, whose height is bounded by the panel's 100vh column.
    card: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      boxSizing: 'border-box',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      overflow: 'hidden'
    },
    header: {
      flexShrink: 0,
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '8px',
      padding: isPhone ? '8px 10px' : '10px 14px',
      borderBottom: `1px solid ${tokens.colors.borderSecondary}`
    },
    body: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      overflowX: 'hidden',
      scrollbarWidth: 'thin',
      padding: isPhone ? '4px 10px 12px' : '4px 14px 14px'
    },
    title: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: theme?.text?.primary || '#1F2937'
    },
    buttonGroup: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap'
    },
    // Owner heading: the section marker. Items sit indented beneath it.
    groupHeading: {
      fontSize: '14px',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: theme?.text?.primary || '#1F2937',
      margin: '14px 0 2px'
    },
    sectionHeading: (active) => ({
      fontSize: '12px',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: active ? (theme?.primary || tokens.colors.textSecondary) : tokens.colors.textSecondary,
      padding: '6px 0 2px',
      marginLeft: isPhone ? '8px' : '14px'
    }),
    section: (active) => ({
      marginLeft: isPhone ? '8px' : '14px',
      paddingLeft: isPhone ? '8px' : '14px',
      minHeight: dragging ? '14px' : 0,
      boxShadow: active ? `inset 0 -2px 0 ${theme?.primary || tokens.colors.textSecondary}` : 'none'
    }),
    form: {
      backgroundColor: theme?.bg || '#F5F5F5',
      padding: isPhone ? '12px' : '16px',
      borderRadius: '8px',
      margin: '10px 0'
    },
    formGrid: {
      display: 'grid',
      gridTemplateColumns: isPhone ? '1fr 1fr' : 'repeat(4, 1fr)',
      gap: '12px',
      marginBottom: '12px'
    },
    formRow: {
      marginBottom: '12px'
    },
    label: {
      display: 'block',
      fontSize: '13px',
      fontWeight: '500',
      marginBottom: '4px',
      color: tokens.colors.textSecondary
    },
    // 16px on phones stops iOS zooming into the field on focus.
    input: {
      width: '100%',
      boxSizing: 'border-box',
      padding: isPhone ? '10px 12px' : '8px 12px',
      borderRadius: '6px',
      border: `1px solid ${tokens.colors.borderSecondary}`,
      fontSize: isPhone ? '16px' : '13px',
      backgroundColor: 'white'
    },
    textarea: {
      width: '100%',
      boxSizing: 'border-box',
      padding: '8px 12px',
      borderRadius: '6px',
      border: `1px solid ${tokens.colors.borderSecondary}`,
      fontSize: isPhone ? '16px' : '13px',
      minHeight: '72px',
      fontFamily: 'inherit'
    },
    // No borders and no gaps between items (Michael, 21 Sep): the indent, the headings and the
    // label line separate them. The inset line appears only while a drag is over the row.
    item: (lineAbove, lineBelow, isDragged) => ({
      padding: '3px 0',
      opacity: isDragged ? 0.4 : 1,
      boxShadow: lineAbove
        ? `inset 0 2px 0 ${theme?.primary || tokens.colors.textSecondary}`
        : lineBelow
          ? `inset 0 -2px 0 ${theme?.primary || tokens.colors.textSecondary}`
          : 'none'
    }),
    itemLine: {
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      columnGap: '8px',
      rowGap: '4px'
    },
    handle: {
      display: 'inline-flex',
      alignItems: 'center',
      cursor: 'grab',
      color: tokens.colors.textSecondary,
      flexShrink: 0
    },
    itemNo: {
      fontSize: '13px',
      fontWeight: 'bold',
      color: tokens.colors.textSecondary,
      flexShrink: 0
    },
    itemTitle: {
      flex: '1 1 240px',
      minWidth: 0,
      fontSize: '14px',
      fontWeight: 'bold',
      color: theme?.text?.primary || '#1F2937',
      overflowWrap: 'anywhere'
    },
    meta: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '12px',
      color: tokens.colors.textSecondary,
      flexShrink: 0
    },
    badge: (color) => ({
      display: 'inline-block',
      padding: '1px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500',
      backgroundColor: color.bg,
      color: color.text
    }),
    controls: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      flexShrink: 0
    },
    refEditRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      marginBottom: '4px'
    },
    refEditText: {
      flex: '1 1 auto',
      minWidth: 0,
      fontSize: '12px',
      color: tokens.colors.textSecondary,
      overflowWrap: 'anywhere'
    },
    refLink: {
      color: theme?.primary || tokens.colors.textSecondary
    },
    refAddRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    },
    refInput: {
      flex: '1 1 auto',
      minWidth: 0,
      boxSizing: 'border-box',
      padding: isPhone ? '10px 12px' : '8px 12px',
      borderRadius: '6px',
      border: `1px solid ${tokens.colors.borderSecondary}`,
      fontSize: isPhone ? '16px' : '13px',
      backgroundColor: 'white'
    },
    statusSelect: {
      padding: isPhone ? '6px 8px' : '3px 6px',
      borderRadius: '6px',
      border: `1px solid ${tokens.colors.borderSecondary}`,
      fontSize: isPhone ? '16px' : '12px',
      backgroundColor: 'white'
    },
    empty: {
      textAlign: 'center',
      padding: '40px',
      color: tokens.colors.textSecondary
    }
  };

  const renderSelect = (field, label, values) => (
    <div>
      <label style={styles.label}>{label}</label>
      <select
        value={formData[field]}
        onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
        style={styles.input}
      >
        {values.map(value => (
          <option key={value} value={value}>{labels[field][value]}</option>
        ))}
      </select>
    </div>
  );

  const renderForm = (onSubmit, submitLabel, onCancel) => (
    <div style={styles.form}>
      <div style={styles.formRow}>
        <label style={styles.label}>{t('title')}</label>
        <input
          type="text"
          value={formData.title}
          maxLength={200}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          style={styles.input}
          placeholder={t('enterTitle')}
        />
      </div>
      <div style={styles.formGrid}>
        {renderSelect('owner', t('roadmapOwner'), OWNERS)}
        {renderSelect('kind', t('roadmapKind'), KINDS)}
        {renderSelect('priority', t('roadmapPriority'), PRIORITIES)}
        {renderSelect('status', t('status'), STATUSES)}
      </div>
      <div style={styles.formRow}>
        <label style={styles.label}>{t('roadmapRefs')}</label>
        {formData.refs.map((entry, index) => (
          <div key={`${index}-${entry}`} style={styles.refEditRow}>
            <span style={styles.refEditText}>
              {(() => {
                const repo = splitRepoPath(entry);
                if (!repo) return entry;
                return (
                  <>
                    <a
                      href={REPO_FILE_BASE + encodeURI(repo.path)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.refLink}
                    >
                      {repo.path}
                    </a>
                    {repo.rest}
                  </>
                );
              })()}
            </span>
            {/* Labels, not icons, and `ghost` rather than `inline`. Two reasons, both found the
                hard way — these three controls shipped on 22 Sep and Michael could not see them.
                (a) LAYOUT.md § Usage Rule scopes `inline` to no-container affordances that live
                inside CONTENT (Like, Reply, Flag); a control inside an edit form, between a
                `utility` Add and an `action` Save, is chrome, and chrome is the container-bearing
                family. `ghost` is the only semantic variant that is container-bearing AND smaller
                than a pill, so it suits a 12px text row.
                (b) No icon is used at all, because StandardButton's icon registry holds no
                complete up/down pair — `ChevronDown` and `ArrowUp` are registered, `ChevronUp` and
                `ArrowDown` are not. An unknown name resolves to null silently, which is why the
                old move-up control rendered an empty, zero-size, unclickable button. Labels
                sidestep that entirely and are plainer than any glyph. The registry gap is flagged
                to docs/reuse-backlog.md, not fixed here. */}
            <StandardButton
              variant="ghost"
              label={t('roadmapMoveRefUp')}
              title={t('roadmapMoveRefUp')}
              disabled={index === 0}
              onClick={() => moveRef(index, -1)}
            />
            <StandardButton
              variant="ghost"
              label={t('roadmapMoveRefDown')}
              title={t('roadmapMoveRefDown')}
              disabled={index === formData.refs.length - 1}
              onClick={() => moveRef(index, 1)}
            />
            <StandardButton
              variant="ghost"
              label={t('roadmapRemoveRef')}
              title={t('roadmapRemoveRef')}
              onClick={() => removeRef(index)}
            />
          </div>
        ))}
        <div style={styles.refAddRow}>
          <input
            type="text"
            value={refDraft}
            onChange={(e) => setRefDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRef(); } }}
            style={styles.refInput}
            placeholder={t('roadmapRefsPlaceholder')}
          />
          <StandardButton
            variant="utility"
            label={t('roadmapAddRef')}
            onClick={addRef}
            disabled={!refDraft.trim()}
          />
        </div>
      </div>
      <div style={styles.formRow}>
        <label style={styles.label}>{t('notes')}</label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          style={styles.textarea}
          placeholder={t('addNotes')}
        />
      </div>
      <div style={styles.buttonGroup}>
        <StandardButton variant="action" label={submitLabel} onClick={onSubmit} disabled={!formData.title.trim()} />
        <StandardButton variant="utility" label={t('cancel')} onClick={onCancel} />
      </div>
    </div>
  );

  // One line per item where it fits: handle · #no · title · status · kind · controls. The
  // priority label is gone: the item already sits under its now / next / later heading.
  // Done items keep their date in place of a section.
  const renderItem = (item, { draggableRow = false } = {}) => {
    if (editingItem === item.id) {
      return (
        <div key={item.id}>
          {renderForm(() => handleUpdate(item.id), t('save'), cancelEdit)}
        </div>
      );
    }

    const isDragged = dragging?.id === item.id;
    const lineAbove = dropTarget?.lineOn === item.id && !dropTarget.after;
    const lineBelow = dropTarget?.lineOn === item.id && dropTarget.after;

    return (
      <div
        key={item.id}
        style={styles.item(lineAbove, lineBelow, isDragged)}
      >
        <div style={styles.itemLine}>
          {draggableRow && (
            <span
              draggable
              onDragStart={(e) => {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData(DRAG_TYPE, item.id);
                const row = e.currentTarget.closest('[data-roadmap-row]');
                if (row) e.dataTransfer.setDragImage(row, 12, 12);
                // Deferred one tick: changing the layout inside dragstart (empty sections appear,
                // the row dims) can shift things under the pointer or cancel the drag.
                setTimeout(() => setDragging(item), 0);
              }}
              onDragEnd={() => { setDragging(null); setDropTarget(null); }}
              style={styles.handle}
              title={t('roadmapDragHandle')}
              aria-label={t('roadmapDragHandle')}
            >
              <GripVertical size={14} />
            </span>
          )}
          <span style={styles.itemNo}>#{item.item_no}</span>
          <span style={styles.itemTitle}>{item.title}</span>
          <span style={styles.meta}>
            <span style={styles.badge(STATUS_COLORS[item.status] || STATUS_COLORS.done)}>
              {labels.status[item.status] || item.status}
            </span>
            <span>·</span>
            <span>{labels.kind[item.kind] || item.kind}</span>
            {item.status === 'done' && item.done_at && (
              <>
                <span>·</span>
                <span>{new Date(item.done_at).toLocaleDateString(formattingLocale)}</span>
              </>
            )}
          </span>
          <span style={styles.controls}>
            <select
              value={item.status}
              onChange={(e) => updateItem(item.id, { status: e.target.value })}
              style={styles.statusSelect}
              aria-label={t('status')}
            >
              {STATUSES.map(status => (
                <option key={status} value={status}>{labels.status[status]}</option>
              ))}
            </select>
            <StandardButton variant="utility" iconName="Pencil" label={t('edit')} onClick={() => startEdit(item)} />
            {/* Stays the destructive pill: StandardButton's inline branch renders no confirmation
                modal, so an inline Delete would either do nothing or skip the confirmation. */}
            {/* The confirmation names the item: the table keeps no history and a delete is
                permanent, so a dialog that does not say which row is not a protection.
                The existing key is REWRITTEN rather than a new one minted — translationService
                returns the key itself when a key is missing, so a new key whose batch had not
                reached production would put `admin.deleteRoadmapItem…` in front of an
                irreversible delete; rewriting this one degrades to its old English instead.
                No truncation: title is capped at 200 by both the column and the form, and the
                dialog wraps. */}
            <StandardButton
              variant="destructive"
              iconName="Trash2"
              label={t('delete')}
              confirmMessage={t('deleteRoadmapItemConfirm', { number: item.item_no, title: item.title })}
              onClick={() => handleDelete(item.id)}
            />
          </span>
        </div>
        {/* The list shows each item's title line and nothing else (Michael's ruling, 23 Sep):
            references and notes are visible in Edit. */}
      </div>
    );
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>{t('roadmapMaintenanceLog')}</div>
        <div style={styles.buttonGroup}>
          <StandardButton variant="utility" iconName="RefreshCw" label={t('refresh')} onClick={() => loadItems()} />
          <StandardButton
            variant="action"
            iconName="Plus"
            label={t('addEntry')}
            onClick={() => { cancelEdit(); setShowAddForm(!showAddForm); }}
          />
        </div>
      </div>

      <div style={styles.body}>
        {showAddForm && renderForm(handleAdd, t('add'), () => { setShowAddForm(false); setFormData(emptyForm()); setRefDraft(''); })}

        {loading ? (
          <div style={styles.empty}>{t('loadingRoadmap')}</div>
        ) : (
          <>
            {groups.length === 0 ? (
              <div style={styles.empty}>{t('noRoadmapItems')}</div>
            ) : (
              groups.map(group => (
                <div key={group.owner}>
                  <div style={styles.groupHeading}>
                    {labels.owner[group.owner]} · {group.count}
                  </div>
                  {group.sections.map(section => {
                    // Empty sections show only while this owner's item is being dragged, so
                    // there is somewhere to drop into.
                    const dropHere = dragging?.owner === group.owner;
                    if (section.items.length === 0 && !dropHere) return null;
                    const endActive = dropTarget?.key === `${group.owner}|${section.priority}|end`;
                    return (
                      <div key={section.priority}>
                        <div style={styles.sectionHeading(dropHere && dropTarget?.priority === section.priority)}>
                          {labels.priority[section.priority]}
                        </div>
                        <div
                          style={styles.section(endActive)}
                          {...(dropHere ? sectionDragProps(group.owner, section.priority) : {})}
                        >
                          {section.items.map(item => (
                            <div key={item.id} data-roadmap-row data-item-id={item.id}>
                              {renderItem(item, { draggableRow: canDrag })}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}

            {doneItems.length > 0 && (
              <div style={{ marginTop: '14px' }}>
                <StandardButton
                  variant="utility"
                  label={showDone
                    ? t('roadmapHideDone')
                    : t('roadmapShowDone', { count: doneItems.length })}
                  onClick={() => setShowDone(!showDone)}
                />
                {showDone && (
                  <div>
                    <div style={styles.groupHeading}>{labels.status.done}</div>
                    <div style={styles.section(false)}>
                      {doneItems.map(item => renderItem(item))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <ConfirmationModal
        visible={infoModal.visible}
        message={infoModal.message}
        onOk={() => setInfoModal({ visible: false, message: '' })}
      />
    </div>
  );
};

export default RoadmapTab;
